// Fetch et parsing des données de la page école

import type { ComputedRef, MaybeRefOrGetter } from 'vue'
import { computed, ref, toValue } from 'vue'
import { getAxiosResponseStatus } from '~/utils/axios'
import type {
  BlogArticleObj,
  CampusBlock,
  DifferentiatorObj,
  DirectorBlock,
  MetricObj,
  MetricsBlock,
  PartnerObj,
  SchoolData,
  SchoolDataResponse,
  SeoSiblingsBlock,
  StatsBlock,
} from '~/types/school'
import {
  EMPTY_CAMPUS_BLOCK,
  EMPTY_DIRECTOR_BLOCK,
  EMPTY_METRICS_BLOCK,
  EMPTY_SEO_BLOCK,
  EMPTY_STATS_BLOCK,
} from '~/constants/empty'
import { parseBlock } from '~/utils/helpers'

interface UseSchoolDataOptions {
  language: MaybeRefOrGetter<string>
  alias: MaybeRefOrGetter<string>
}

/**
 * Construit l'URL d'embed YouTube à partir d'une URL classique.
 * @param {string} youtubeUrl URL YouTube (watch/youtu.be/embed).
 * @returns {string} URL d'embed (ou chaîne vide si invalide).
 */
function getYouTubeEmbedUrl(youtubeUrl: string): string {
  const trimmed: string = youtubeUrl.trim()
  if (!trimmed) return ''

  const normalized: string = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`

  let videoId = ''
  try {
    if (normalized.includes('youtube.com/watch')) {
      videoId = new URL(normalized).searchParams.get('v') ?? ''
    } else if (normalized.includes('youtu.be')) {
      videoId = normalized.split('/').pop()?.split('?')[0] ?? ''
    } else if (normalized.includes('youtube.com/embed/')) {
      videoId = normalized.split('/').pop()?.split('?')[0] ?? ''
    }
  } catch {
    return ''
  }

  return videoId ? `https://www.youtube.com/embed/${videoId}?rel=0` : ''
}

// ── Composable ────────────────────────────────────────────
export const useSchoolData = async (options: UseSchoolDataOptions) => {
  const api = useFrontApi()
  const lastHttpStatus = ref<number | undefined>(undefined)

  const {
    data: schoolData,
    error,
    status,
    refresh,
  } = await useAsyncData<SchoolDataResponse | null>(
    () => `school-data-${toValue(options.language)}-${toValue(options.alias)}`,
    async () => {
      const language = toValue(options.language)
      const alias = toValue(options.alias)

      if (!alias || !language) {
        lastHttpStatus.value = undefined
        return null
      }

      const segments = alias.split('/')
      if (segments.length > 3) {
        console.warn(`Alias inattendu : ${alias}. Fetch annulé.`)
        return null
      }
      if (segments.length !== 1) return null // ne gère que les pages école (1 segment)

      try {
        const endpoint: string = `/api/aca/schooldata?language=${language}&alias=${alias}`
        const { data } = await api.get<SchoolData>(endpoint)
        data.youTubeUrl = getYouTubeEmbedUrl(data.youTubeUrl)
        const metricsBlock: MetricsBlock = parseBlock<MetricsBlock>(
          data.metricsBlock,
          EMPTY_METRICS_BLOCK,
        )

        lastHttpStatus.value = 200

        return {
          schooldatas: data,
          parsedParentBlock: parseBlock<SeoSiblingsBlock>(data.parentBlock, EMPTY_SEO_BLOCK),
          parsedSchoolTypeBlock: parseBlock<SeoSiblingsBlock>(
            data.schoolTypeBlock,
            EMPTY_SEO_BLOCK,
          ),
          parsedCountriesBlock: parseBlock<SeoSiblingsBlock>(data.countriesBlock, EMPTY_SEO_BLOCK),
          parsedCitiesBlock: parseBlock<SeoSiblingsBlock>(data.citiesBlock, EMPTY_SEO_BLOCK),
          parsedProgramsBlock: parseBlock<SeoSiblingsBlock>(data.programsBlock, EMPTY_SEO_BLOCK),
          parsedDegreesBlock: parseBlock<SeoSiblingsBlock>(data.degreesBlock, EMPTY_SEO_BLOCK),
          parsedMetricsBlock: {
            ...metricsBlock,
            Metrics: metricsBlock.Metrics.filter(
              (metric: MetricObj): boolean => (metric.Value ?? '').trim() !== '',
            ),
          },
          parsedDirectorBlock: parseBlock<DirectorBlock>(data.directorBlock, EMPTY_DIRECTOR_BLOCK),
          parsedStatsBlock: parseBlock<StatsBlock>(data.statsBlock, EMPTY_STATS_BLOCK),
          parsedAddressesBlock: parseBlock<CampusBlock>(data.addressesBlock, EMPTY_CAMPUS_BLOCK),
          parsedDifferentiators: parseBlock<DifferentiatorObj[]>(
            data.differentiatorsBlock,
            [],
          ).filter(
            (differentiator: DifferentiatorObj): boolean => differentiator.Title.trim() !== '',
          ),
          parsedPartners: parseBlock<PartnerObj[]>(data.partners, []),
          programs: [], // TODO : brancher l'API programmes liés
          parsedArticles: parseBlock<BlogArticleObj[]>(data.articles, []),
        }
      } catch (e) {
        lastHttpStatus.value = getAxiosResponseStatus(e)
        return null
      }
    },
    {
      watch: [() => toValue(options.language), () => toValue(options.alias)],
    },
  )

  const loading: ComputedRef<boolean> = computed<boolean>(() => status.value === 'pending')

  return {
    schoolData,
    loading,
    error: computed(() => (error.value ? (error.value.message ?? 'Erreur inconnue') : null)),
    lastHttpStatus: computed(() => lastHttpStatus.value),
    refresh,
  }
}
